<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import { CalendarDaysIcon, ClipboardDocumentListIcon, EyeIcon, TruckIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer, UiTimeline } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import { formatDate, formatDateTime } from "@/utils/date"

const { confirm } = useConfirmation()
const notify = useNotifications()

type PurchaseOrderRow = {
  id: number
  internal_reference_code: string
  company: string
  supplier: string
  status: string
  raw_status: string
  status_badge: { key: string; tone: string; label: string }
  created_at: string
  expected_at: string | null
  ordered_at: string | null
  received_at: string | null
  cancelled_at: string | null
  created_by: string
  ordered_by: string
  received_by: string
  cancelled_by: string
  notes: string
  total_amount: string
  items_count: number
  items: Array<{
    product: string
    quantity: string
    unit_price: string
  }>
  can_edit: boolean
  can_destroy: boolean
  can_order: boolean
  can_receive: boolean
  can_cancel: boolean
}

type TimelineStep = {
  id: string
  title: string
  description: string
  icon: "create" | "send" | "receive" | "factory" | "done" | "cancel" | "clock"
  state: "done" | "pending" | "warning" | "danger"
}

const props = defineProps<{
  purchase_orders: PurchaseOrderRow[]
  companies: Array<{ id: number; name: string }>
  statuses: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    status?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const columns: UiDataTableColumn[] = [
  { key: "internal_reference_code", label: "Referencia" },
  { key: "company", label: "Empresa" },
  { key: "supplier", label: "Fornecedor" },
  { key: "status", label: "Status", align: "center" },
  { key: "items_count", label: "Itens", align: "center" },
  { key: "total_amount", label: "Total", align: "right" },
  { key: "expected_at", label: "Previsao", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
  {
    key: "status",
    label: "Status",
    options: props.statuses,
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
  status: props.query.status ?? "",
})

const isDetailsDrawerOpen = ref(false)
const selectedOrder = ref<PurchaseOrderRow | null>(null)

const statusBadgeClassByTone: Record<string, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-rose-50 text-rose-700 border-rose-200",
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
}

function statusBadgeClass(tone: string): string {
  return statusBadgeClassByTone[tone] ?? statusBadgeClassByTone.neutral
}

function openDetailsDrawer(order: PurchaseOrderRow) {
  selectedOrder.value = order
  isDetailsDrawerOpen.value = true
}

function isCancelledStatus(status: string): boolean {
  return status.toLowerCase().includes("cancelado")
}

function purchaseTimelineSteps(order: PurchaseOrderRow): TimelineStep[] {
  const steps: TimelineStep[] = [
    {
      id: "created",
      title: "Rascunho criado",
      description: `${formatDateTime(order.created_at)} por ${order.created_by || "Nao informado"}`,
      icon: "create",
      state: "done",
    },
    {
      id: "ordered",
      title: "Pedido enviado",
      description: order.ordered_at ? `${formatDateTime(order.ordered_at)} por ${order.ordered_by || "Nao informado"}` : "Pendente",
      icon: "send",
      state: order.ordered_at ? "done" : "pending",
    },
    {
      id: "received",
      title: "Pedido recebido",
      description: order.received_at ? `${formatDateTime(order.received_at)} por ${order.received_by || "Nao informado"}` : "Pendente",
      icon: "receive",
      state: order.received_at ? "done" : "pending",
    },
    {
      id: "cancelled",
      title: "Pedido cancelado",
      description: order.cancelled_at
        ? `${formatDateTime(order.cancelled_at)} por ${order.cancelled_by || "Nao informado"}`
        : (isCancelledStatus(order.status) ? "Sem registro de cancelamento" : "Nao cancelado"),
      icon: "cancel",
      state: order.cancelled_at ? "danger" : "pending",
    },
  ]

  if (!order.received_at) {
    steps.push({
      id: "expected",
      title: "Previsao de entrega",
      description: order.expected_at ? formatDate(order.expected_at) : "Nao informada",
      icon: "clock",
      state: order.expected_at ? "warning" : "pending",
    })
  }

  return steps
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    status: String(overrides.status ?? selectedFilters.status ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/purchase_orders", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyOrder(id: number) {
  const ok = await confirm({
    title: "Excluir pedido",
    message: "Deseja excluir este pedido de compra?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/purchase_orders/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Pedido excluido com sucesso"),
  })
}

function orderPurchaseOrder(id: number) {
  router.patch(`/purchase_orders/${id}/order`, {}, {
    onSuccess: () => notify.success("Pedido enviado com sucesso"),
  })
}

function receivePurchaseOrder(id: number) {
  router.patch(`/purchase_orders/${id}/receive`, {}, {
    onSuccess: () => notify.success("Pedido recebido com sucesso"),
  })
}

function cancelPurchaseOrder(id: number) {
  router.patch(`/purchase_orders/${id}/cancel`, {}, {
    onSuccess: () => notify.success("Pedido cancelado"),
  })
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Pedidos de compra</h1>
        <p class="mt-2 text-sm text-slate-600">Gerencie pedidos de compra e gere entradas de estoque ao receber.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="purchase_orders"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/purchase_orders/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo pedido
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar pedido"
              @click="openDetailsDrawer(row as PurchaseOrderRow)"
            >
              <EyeIcon class="size-4" />
            </button>

            <Link
              v-if="(row as PurchaseOrderRow).can_edit"
              :href="`/purchase_orders/${(row as PurchaseOrderRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as PurchaseOrderRow).can_order" size="sm" variant="secondary" @click="orderPurchaseOrder((row as PurchaseOrderRow).id)">
              Enviar
            </UiButton>

            <UiButton v-if="(row as PurchaseOrderRow).can_receive" size="sm" variant="primary" @click="receivePurchaseOrder((row as PurchaseOrderRow).id)">
              Receber
            </UiButton>

            <UiButton v-if="(row as PurchaseOrderRow).can_cancel" size="sm" variant="ghost" @click="cancelPurchaseOrder((row as PurchaseOrderRow).id)">
              Cancelar
            </UiButton>

            <UiButton v-if="(row as PurchaseOrderRow).can_destroy" size="sm" variant="danger" @click="destroyOrder((row as PurchaseOrderRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else-if="column.key === 'expected_at'">{{ formatDate((row as PurchaseOrderRow).expected_at) }}</span>
          <span
            v-else-if="column.key === 'status'"
            class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
            :class="statusBadgeClass((row as PurchaseOrderRow).status_badge.tone)"
          >
            {{ (row as PurchaseOrderRow).status_badge.label }}
          </span>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isDetailsDrawerOpen" title="Detalhes do pedido" size="2xl">
        <div v-if="selectedOrder" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-xs uppercase tracking-wide text-slate-500">Pedido de compra</p>
                <h3 class="text-lg font-semibold text-slate-900">{{ selectedOrder.internal_reference_code }} - {{ selectedOrder.supplier }}</h3>
                <p class="mt-1 text-sm text-slate-600">Empresa: {{ selectedOrder.company }}</p>
              </div>

              <span class="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold" :class="statusBadgeClass(selectedOrder.status_badge.tone)">
                {{ selectedOrder.status_badge.label }}
              </span>
            </div>

            <div class="mt-4 grid gap-3 md:grid-cols-3">
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Total</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ selectedOrder.total_amount }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Itens</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ selectedOrder.items_count }}</p>
              </article>

              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Previsao</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ formatDate(selectedOrder.expected_at) }}</p>
              </article>
            </div>
          </section>

          <section class="grid gap-3 md:grid-cols-2">
            <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p class="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <CalendarDaysIcon class="size-4 text-slate-500" /> Timeline do pedido
              </p>

              <UiTimeline :steps="purchaseTimelineSteps(selectedOrder)" />
            </article>

            <article class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <TruckIcon class="size-4 text-slate-500" /> Fornecedor
              </p>
              <p class="text-sm text-slate-700">Nome: <span class="font-medium">{{ selectedOrder.supplier }}</span></p>
              <p class="text-sm text-slate-700">Empresa: <span class="font-medium">{{ selectedOrder.company }}</span></p>
            </article>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h3 class="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ClipboardDocumentListIcon class="size-4 text-slate-500" /> Itens do pedido
            </h3>

            <div class="overflow-x-auto rounded-md border border-slate-200">
              <table class="min-w-full border-collapse">
                <thead class="bg-slate-50">
                  <tr class="border-b border-slate-200">
                    <th class="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Produto</th>
                    <th class="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Quantidade</th>
                    <th class="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Preco unitario</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="selectedOrder.items.length === 0">
                    <td colspan="3" class="px-3 py-6 text-center text-sm text-slate-500">Sem itens no pedido.</td>
                  </tr>
                  <tr v-for="(item, index) in selectedOrder.items" v-else :key="`${item.product}-${index}`" class="border-b border-slate-100 odd:bg-white even:bg-slate-50/40">
                    <td class="px-3 py-2 text-sm text-slate-700">{{ item.product }}</td>
                    <td class="px-3 py-2 text-right text-sm text-slate-700">{{ item.quantity }}</td>
                    <td class="px-3 py-2 text-right text-sm text-slate-700">{{ item.unit_price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-3 flex justify-end">
              <p class="rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Total do pedido: {{ selectedOrder.total_amount }}</p>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h3 class="mb-2 text-sm font-semibold text-slate-800">Observacoes</h3>
            <p class="text-sm text-slate-700">{{ selectedOrder.notes || "Sem observacoes informadas." }}</p>
          </section>
        </div>
      </UiDrawer>
    </div>
  </AppShell>
</template>

